import React, { useEffect, useState } from "react";
import { Send, Bell, Users, Globe2, UserRound, CheckCheck, Eye, Clock } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { programs } from "../../mock/mock";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import ScheduledManager from "./ScheduledManager";
import { AudiencePicker } from "../../components/admin/AudiencePicker";

const PROGRAM_NAMES = programs.map((p) => p.name);

export default function NotificacionesAdmin() {
  const [form, setForm] = useState({ title: "", body: "", url: "/mi-cuenta", target: "all", programa: PROGRAM_NAMES[0], user_ids: [], programas: ["Todos"], grupos: ["Todos"] });
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [tab, setTab] = useState("ahora");

  const load = () => adminApi.pushCampaigns().then(setCampaigns).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    adminApi.listUsers().then((u) => setUsers(u.filter((x) => x.role !== "admin"))).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Completa título y mensaje."); return; }
    setSending(true);
    try {
      const payload = { title: form.title, body: form.body, url: form.url || "/mi-cuenta", target: form.target };
      if (form.target === "segmento") {
        if (!form.programas.length || !form.grupos.length) { toast.error("Elige al menos un programa y un grupo."); setSending(false); return; }
        payload.programas = form.programas; payload.grupos = form.grupos;
      }
      if (form.target === "usuarios") payload.user_ids = form.user_ids;
      const r = await adminApi.pushSend(payload);
      toast.success(`Enviada a ${r.enviados} de ${r.destinatarios} destinatarios.`);
      setForm({ title: "", body: "", url: "/mi-cuenta", target: "all", programa: PROGRAM_NAMES[0], user_ids: [], programas: ["Todos"], grupos: ["Todos"] });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const openDetail = async (c) => {
    setDetail(c);
    try { setReceipts(await adminApi.pushReceipts(c.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleUser = (id) => setForm((f) => ({ ...f, user_ids: f.user_ids.includes(id) ? f.user_ids.filter((x) => x !== id) : [...f.user_ids, id] }));

  const TARGETS = [["all", "Todos", Globe2], ["segmento", "Por programa y grupo", Users], ["usuarios", "Usuarios específicos", UserRound]];

  return (
    <div data-testid="notificaciones-admin">
      <h1 className="text-2xl font-extrabold text-[#0A1C30] flex items-center gap-2"><Bell className="w-6 h-6 text-[#E9A400]" /> Notificaciones Push</h1>
      <p className="text-sm text-[#6B7688] mt-1">Envía avisos que llegan aunque la app esté cerrada y revisa quién los vio.</p>

      <div className="mt-5 flex gap-2 border-b border-black/5">
        {[["ahora", "Enviar ahora"], ["programadas", "Programadas"]].map(([v, l]) => (
          <button key={v} data-testid={`notif-tab-${v}`} onClick={() => setTab(v)} className={`px-4 py-2.5 font-bold text-sm -mb-px border-b-2 transition-colors ${tab === v ? "border-[var(--gold)] text-[#0A1C30]" : "border-transparent text-[#6B7688] hover:text-[#0A1C30]"}`}>{l}</button>
        ))}
      </div>

      {tab === "programadas" ? (
        <ScheduledManager users={users} />
      ) : (
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6">
          <h2 className="font-extrabold text-[#0A1C30] mb-4">Nuevo mensaje</h2>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block font-bold">Título</Label><Input data-testid="push-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="¡Nueva clase en vivo!" /></div>
            <div><Label className="mb-1.5 block font-bold">Mensaje</Label>
              <textarea data-testid="push-body-input" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className="w-full min-h-[90px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder="Te esperamos hoy a las 6pm en Campus Supera." />
            </div>
            <div><Label className="mb-1.5 block font-bold">Enviar a</Label>
              <div className="flex gap-2">
                {TARGETS.map(([v, l, Ic]) => (
                  <button key={v} data-testid={`target-${v}`} onClick={() => setForm((f) => ({ ...f, target: v }))} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 font-bold text-xs transition-all ${form.target === v ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30] hover:bg-[#EDF0F4]"}`}>
                    <Ic className="w-4 h-4" /> {l}
                  </button>
                ))}
              </div>
            </div>
            {form.target === "segmento" && (
              <AudiencePicker testid="push-aud" programas={form.programas} grupos={form.grupos} onChange={(v) => setForm((f) => ({ ...f, ...v }))} />
            )}
            {form.target === "usuarios" && (
              <div className="max-h-48 overflow-y-auto rounded-xl ring-1 ring-black/5 divide-y divide-black/5">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F4F6F9]">
                    <input type="checkbox" checked={form.user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} />
                    <span className="font-semibold text-[#0A1C30]">{u.nombre} {u.apellidos}</span>
                    <span className="text-xs text-[#6B7688]">· {u.email}</span>
                  </label>
                ))}
                {users.length === 0 && <p className="px-3 py-4 text-sm text-[#6B7688]">No hay usuarios.</p>}
              </div>
            )}
            <button data-testid="push-send-btn" onClick={send} disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold py-3 transition-colors disabled:opacity-60">
              <Send className="w-5 h-5" /> {sending ? "Enviando..." : "Enviar notificación"}
            </button>
          </div>
        </div>

        {/* History */}
        <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6">
          <h2 className="font-extrabold text-[#0A1C30] mb-4">Historial y confirmaciones</h2>
          <div className="space-y-3" data-testid="campaigns-list">
            {campaigns.map((c) => (
              <button key={c.id} data-testid={`campaign-${c.id}`} onClick={() => openDetail(c)} className="w-full text-left rounded-2xl ring-1 ring-black/5 p-4 hover:ring-[var(--gold)] transition-all">
                <p className="font-extrabold text-[#0A1C30]">{c.title}</p>
                <p className="text-sm text-[#6B7688] line-clamp-1">{c.body}</p>
                <div className="mt-2 flex items-center gap-4 text-xs font-bold">
                  <span className="inline-flex items-center gap-1 text-[#3B7DF6]"><Send className="w-3.5 h-3.5" /> {c.total_enviados} enviados</span>
                  <span className="inline-flex items-center gap-1 text-[#22A860]"><Eye className="w-3.5 h-3.5" /> {c.leidos} vistos</span>
                </div>
              </button>
            ))}
            {campaigns.length === 0 && <p className="text-sm text-[#6B7688]">Aún no has enviado notificaciones.</p>}
          </div>
        </div>
      </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7688] -mt-2">{detail?.body}</p>
          <div className="mt-3 max-h-80 overflow-y-auto divide-y divide-black/5" data-testid="receipts-list">
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-bold text-[#0A1C30]">{r.user_name || r.user_email}</p>
                  <p className="text-xs text-[#6B7688]">{r.programa}</p>
                </div>
                {r.read_at ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#22A860]"><CheckCheck className="w-4 h-4" /> Visto</span>
                ) : r.delivered_at ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#3B7DF6]"><CheckCheck className="w-4 h-4" /> Entregado</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#B0B6C0]"><Clock className="w-4 h-4" /> Pendiente</span>
                )}
              </div>
            ))}
            {receipts.length === 0 && <p className="py-4 text-sm text-[#6B7688]">Sin destinatarios con dispositivo suscrito.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
